import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, BadgeCheck, ExternalLink, Youtube, Loader2, FileText } from "lucide-react";

const STATUSES = [
  { v: "pending_review", label: "Pending" },
  { v: "approved", label: "Approved" },
  { v: "rejected", label: "Rejected" },
  { v: "all", label: "All" },
];

export default function AdminNewsSubmissions() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending_review");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/news/submissions", { params: { status, limit: 200 } });
      setItems(data.items);
    } catch (e) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const doAction = async (id, action) => {
    setBusy(true);
    try {
      await http.post(`/admin/news/submissions/${id}/${action}`, { note });
      toast.success(action === "approve" ? "Published to Things to Know" : "Rejected");
      setActive(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-news-submissions">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-2">NEWS SUBMISSIONS</div>
          <h2 className="font-display text-3xl">Community news queue</h2>
          <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            Registered-user posts awaiting your review. Trusted publishers auto-publish and don't appear here.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {STATUSES.map((s) => (
          <button
            key={s.v}
            data-testid={`ns-filter-${s.v}`}
            onClick={() => setStatus(s.v)}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${status === s.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={status === s.v ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : { border: "1px solid var(--wofy-border)" }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>No submissions in this bucket.</p>
      ) : (
        <div className="grid gap-4">
          {items.map((s) => (
            <div key={s.id} data-testid={`ns-row-${s.id}`} className="wofy-card">
              <div className="flex items-start gap-4">
                <div className="w-24 h-24 rounded-lg overflow-hidden bg-[var(--wofy-card-bg,#f2ede4)] flex items-center justify-center" style={{ border: "1px solid var(--wofy-line)" }}>
                  {s.images?.[0] ? (
                    <img src={s.images[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <FileText className="w-6 h-6" style={{ color: "var(--wofy-primary)" }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded" style={{ background: "var(--wofy-primary)", color: "white" }}>
                      {s.category}
                    </span>
                    {s.subcategory && (<span className="text-[10px]" style={{ color: "var(--wofy-text-soft)" }}>{s.subcategory}</span>)}
                    <span className="text-[10px] font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>
                      {new Date(s.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                    </span>
                  </div>
                  <h3 className="font-display text-lg leading-snug">{s.title}</h3>
                  <p className="text-sm mt-1 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{s.short_description}</p>
                  <div className="mt-2 text-xs flex items-center gap-2 flex-wrap" style={{ color: "var(--wofy-text-soft)" }}>
                    <span>
                      by <b>{s.submitter_name}</b>
                      {s.submitter_trusted && <BadgeCheck className="inline w-3 h-3 ml-1 -mt-0.5" style={{ color: "#1D4ED8" }} />}
                    </span>
                    {s.website_link && (
                      <a href={s.website_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 underline" style={{ color: "var(--wofy-primary)" }}>
                        <ExternalLink className="w-3 h-3" /> source
                      </a>
                    )}
                    {s.youtube_link && (
                      <a href={s.youtube_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 underline" style={{ color: "#B23A3A" }}>
                        <Youtube className="w-3 h-3" /> video
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setActive(s)}
                    data-testid={`ns-review-${s.id}`}
                    className="wofy-btn-ghost text-xs"
                  >
                    Review
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-[#14213D]/25 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="wofy-card w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ns-review-drawer">
            <div className="flex items-center justify-between mb-4">
              <div className="overline">REVIEW SUBMISSION</div>
              <button onClick={() => setActive(null)} className="wofy-btn-ghost text-xs">Close</button>
            </div>
            <h3 className="font-display text-2xl">{active.title}</h3>
            <div className="mt-1 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
              {active.category}{active.subcategory ? ` · ${active.subcategory}` : ""} · by {active.submitter_name} ({active.submitter_email})
            </div>

            {active.images?.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-2">
                {active.images.map((src, i) => (
                  <img key={i} src={src} alt="" className="w-full h-32 object-cover rounded-lg" />
                ))}
              </div>
            )}

            <div className="mt-4">
              <div className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Short description</div>
              <p className="mt-1 text-sm">{active.short_description}</p>
            </div>

            <div className="mt-4">
              <div className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Long article</div>
              <div className="mt-1 text-sm whitespace-pre-wrap">{active.long_article}</div>
            </div>

            {(active.website_link || active.youtube_link) && (
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                {active.website_link && <a href={active.website_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline" style={{ color: "var(--wofy-primary)" }}><ExternalLink className="w-3 h-3" /> {active.website_link}</a>}
                {active.youtube_link && <a href={active.youtube_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline" style={{ color: "#B23A3A" }}><Youtube className="w-3 h-3" /> {active.youtube_link}</a>}
              </div>
            )}

            {active.reviewer_note && (
              <div className="mt-4 rounded-lg border p-3 text-sm" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
                <div className="text-[11px] font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>Note to reviewer</div>
                <p className="mt-1">{active.reviewer_note}</p>
              </div>
            )}

            {active.status === "pending_review" && (
              <div className="mt-6">
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note (approval remark or rejection reason)"
                  rows={2}
                  data-testid="ns-review-note"
                  className="wofy-input"
                />
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    onClick={() => doAction(active.id, "reject")}
                    disabled={busy}
                    data-testid="ns-reject"
                    className="wofy-btn-ghost text-sm"
                    style={{ color: "#B23A3A" }}
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Reject
                  </button>
                  <button
                    onClick={() => doAction(active.id, "approve")}
                    disabled={busy}
                    data-testid="ns-approve"
                    className="wofy-btn-primary text-sm"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve & Publish
                  </button>
                </div>
              </div>
            )}

            {active.status !== "pending_review" && (
              <div className="mt-4 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                Status: <b>{active.status}</b>. Reviewed by {active.reviewed_by || "—"}.
                {active.review_note && <p className="mt-1">Note: {active.review_note}</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
